import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { 
  Shield, ShieldCheck, ShieldAlert, Download, Search, ChevronLeft, ChevronRight,
  ChevronDown, ChevronRight as ChevronRightIcon, Server, Monitor, Filter, X, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateOnlyARG } from '../../lib/utils';

const PatchesReport = () => {
  const [stats, setStats] = useState({ total_installs: 0, unique_kbs: 0, critical_kbs: 0, devices_with_patches: 0 });
  const [activeTab, setActiveTab] = useState('search');
  const [source, setSource] = useState('all');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Filtros
  const [filterOptions, setFilterOptions] = useState({ tipos: [], sites: [], empresas: [] });
  const [filters, setFilters] = useState({
    tipo: '',
    search: '',
    critical_only: false
  });
  
  // === TAB BÚSQUEDA POR KB ===
  const [searchKbId, setSearchKbId] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  
  // Datos Tab Instaladas (listado)
  const [installedData, setInstalledData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [expandedKb, setExpandedKb] = useState(null);
  const [kbDevices, setKbDevices] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [kbDevicesLoading, setKbDevicesLoading] = useState(false);

  const [pageSize] = useState(50);

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/reports/patches/filter-options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await api.get(`/reports/patches/stats?source=${source}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // === BÚSQUEDA DIRECTA POR KB ===
  const handleSearchKb = async () => {
    if (!searchKbId.trim()) {
      toast.error('Ingresa un ID de KB (ej: KB5072653)');
      return;
    }
    
    setSearching(true);
    setSearchResult(null);
    
    try {
      const response = await api.get(`/reports/patches/search/${encodeURIComponent(searchKbId.trim())}?source=${source}`);
      setSearchResult(response.data);
      
      if (response.data.installed_count === 0 && response.data.missing_count === 0) {
        toast.warning('No se encontró información para este KB');
      }
    } catch (error) {
      toast.error('Error al buscar KB');
    } finally {
      setSearching(false);
    }
  };

  // Fetch installed patches
  const fetchInstalled = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        source,
        page: page.toString(),
        page_size: pageSize.toString()
      });
      if (filters.tipo) params.append('tipo', filters.tipo);
      if (filters.search) params.append('search', filters.search);
      if (filters.critical_only) params.append('critical_only', 'true');
      
      const response = await api.get(`/reports/patches/installed?${params}`);
      setInstalledData(response.data);
    } catch (error) {
      toast.error('Error al cargar actualizaciones');
    } finally {
      setLoading(false);
    }
  }, [source, filters, pageSize]);

  // Fetch KB devices (detalle expandido)
  const fetchKbDevices = async (kbId, page = 1) => {
    setKbDevicesLoading(true);
    try {
      const response = await api.get(`/reports/patches/installed/${encodeURIComponent(kbId)}/devices?source=${source}&page=${page}`);
      setKbDevices(response.data);
    } catch (error) {
      console.error('Error fetching KB devices:', error);
    } finally {
      setKbDevicesLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Fetch stats when source changes
  useEffect(() => {
    fetchStats();
  }, [source]);

  // Fetch data when tab or filters change
  useEffect(() => {
    if (activeTab === 'list') {
      fetchInstalled(1);
      setExpandedKb(null);
    }
  }, [activeTab, source, filters, fetchInstalled]);

  // Handle KB expand
  const handleKbExpand = (kbId) => {
    if (expandedKb === kbId) {
      setExpandedKb(null);
    } else {
      setExpandedKb(kbId);
      fetchKbDevices(kbId, 1);
    }
  };

  // Export
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ source, export_type: 'installed' });
      if (filters.tipo) params.append('tipo', filters.tipo);
      
      const response = await api.get(`/reports/patches/export?${params}`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `actualizaciones_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Exportación completada');
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => formatDateOnlyARG(dateStr);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Actualizaciones Instaladas</h1>
            <p className="page-subtitle">Auditoría de parches/KBs en Servidores y Workstations</p>
          </div>
          <Button
            onClick={handleExport}
            disabled={exporting}
            variant="outline"
            data-testid="export-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exportando...' : 'Exportar'}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="patches-stats">
          <div className="stats-card flex items-center gap-3" data-testid="stats-total">
            <Shield className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total_installs.toLocaleString()}</div>
              <div className="stats-card-label">Instalaciones</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-unique">
            <ShieldCheck className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.unique_kbs}</div>
              <div className="stats-card-label">KBs Únicos</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-critical">
            <ShieldAlert className="h-6 w-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-red-500">{stats.critical_kbs}</div>
              <div className="stats-card-label">KBs Críticos</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-devices">
            <Monitor className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-blue-500">{stats.devices_with_patches}</div>
              <div className="stats-card-label">Equipos</div>
            </div>
          </div>
        </div>

        {/* Source Filter - Siempre visible */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Fuente:</span>
              <select
                value={source}
                onChange={(e) => {
                  setSource(e.target.value);
                  setSearchResult(null);
                }}
                className="h-9 text-sm bg-background border border-border rounded px-3"
                data-testid="filter-source"
              >
                <option value="all">Todos</option>
                <option value="servers">Solo Servidores</option>
                <option value="workstations">Solo Workstations</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="search" data-testid="tab-search">
              <Search className="h-4 w-4 mr-2" />
              Buscar por KB
            </TabsTrigger>
            <TabsTrigger value="list" data-testid="tab-list">
              <ShieldCheck className="h-4 w-4 mr-2" />
              Listado ({installedData.total})
            </TabsTrigger>
          </TabsList>

          {/* Tab: Búsqueda por KB */}
          <TabsContent value="search">
            <Card className="card-subtle">
              <CardContent className="p-6">
                {/* Buscador */}
                <div className="flex items-center gap-3 mb-6">
                  <Search className="h-5 w-5 text-muted-foreground" />
                  <Input
                    placeholder="Ingresa el ID del KB (ej: KB5072653)"
                    value={searchKbId}
                    onChange={(e) => setSearchKbId(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchKb()}
                    className="flex-1 max-w-md font-mono"
                    data-testid="search-kb-input"
                  />
                  <Button 
                    onClick={handleSearchKb} 
                    disabled={searching}
                    data-testid="search-kb-btn"
                  >
                    {searching ? 'Buscando...' : 'Buscar'}
                  </Button>
                </div>

                {/* Resultado de búsqueda */}
                {searchResult && (
                  <div className="space-y-6">
                    {/* KBs encontrados */}
                    {searchResult.kbs_found && searchResult.kbs_found.length > 0 && (
                      <div className="bg-muted/30 rounded-lg p-4 border border-border">
                        <div className="text-sm text-muted-foreground mb-2">
                          {searchResult.kbs_found.length} KB{searchResult.kbs_found.length > 1 ? 's' : ''} encontrado{searchResult.kbs_found.length > 1 ? 's' : ''} que coinciden con "{searchResult.search_term}":
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {searchResult.kbs_found.map((kb, idx) => (
                            <div key={idx} className="inline-flex items-center gap-2 bg-background rounded px-2 py-1 border border-border">
                              <span className="font-mono text-sm text-primary">{kb.kb_id}</span>
                              {kb.is_critical && (
                                <span className="badge bg-red-500/10 text-red-500 text-xs">Crítico</span>
                              )}
                              {kb.tipo && (
                                <span className="text-xs text-muted-foreground">{kb.tipo}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Equipos con estos KBs */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span className="font-medium">{searchResult.installed_count} instalaciones encontradas</span>
                      </div>
                      {searchResult.installed && searchResult.installed.length > 0 ? (
                        <div className="border border-border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-left p-3">KB</th>
                                <th className="text-left p-3">EQUIPO</th>
                                <th className="text-left p-3">TIPO</th>
                                <th className="text-left p-3">SITE</th>
                                <th className="text-left p-3">FECHA INSTALACIÓN</th>
                              </tr>
                            </thead>
                            <tbody>
                              {searchResult.installed.map((item, idx) => (
                                <tr key={idx} className="border-t border-border/50 hover:bg-muted/30">
                                  <td className="p-3 font-mono text-primary">{item.kb_id}</td>
                                  <td className="p-3 font-mono">{item.equipo}</td>
                                  <td className="p-3">
                                    {item.tipo_equipo === 'Servidor' ? (
                                      <span className="flex items-center gap-1 text-sm"><Server className="h-4 w-4 text-blue-500" /> Servidor</span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-sm"><Monitor className="h-4 w-4 text-green-500" /> Workstation</span>
                                    )}
                                  </td>
                                  <td className="p-3 text-muted-foreground">{item.site_name || '-'}</td>
                                  <td className="p-3 text-muted-foreground">{formatDate(item.fecha_instalacion)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {searchResult.installed_count > 200 && (
                            <div className="p-2 text-xs text-center text-muted-foreground bg-muted/30">
                              Mostrando 200 de {searchResult.installed_count}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg">
                          <XCircle className="h-10 w-10 mx-auto mb-3 opacity-50" />
                          <p>No se encontraron instalaciones con "{searchResult.search_term}"</p>
                          <p className="text-xs mt-1">Intenta con otro término (ej: 5072, 5066, 5058)</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Estado inicial */}
                {!searchResult && !searching && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Ingresa un ID de KB para ver qué equipos lo tienen instalado</p>
                    <p className="text-xs mt-1">Ejemplo: KB5072653, KB5066130, KB5058526</p>
                  </div>
                )}

                {searching && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Listado de KBs */}
          <TabsContent value="list">
            {/* Filtros adicionales */}
            <Card className="card-subtle mb-4">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <select
                    value={filters.tipo}
                    onChange={(e) => setFilters(prev => ({ ...prev, tipo: e.target.value }))}
                    className="h-9 text-sm bg-background border border-border rounded px-3"
                    data-testid="filter-tipo"
                  >
                    <option value="">Todos los tipos</option>
                    {filterOptions.tipos.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar KB o título..."
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="h-9 w-48"
                      data-testid="filter-search"
                    />
                  </div>
                  
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.critical_only}
                      onChange={(e) => setFilters(prev => ({ ...prev, critical_only: e.target.checked }))}
                      className="rounded border-border"
                    />
                    <ShieldAlert className="h-4 w-4 text-red-500" />
                    Solo críticos
                  </label>
                  
                  {(filters.tipo || filters.search || filters.critical_only) && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setFilters({ tipo: '', search: '', critical_only: false })}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Limpiar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="card-subtle overflow-hidden">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                  </div>
                ) : installedData.items.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No se encontraron actualizaciones</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {installedData.items.map((item) => (
                      <Collapsible 
                        key={item.kb_id} 
                        open={expandedKb === item.kb_id}
                        onOpenChange={() => handleKbExpand(item.kb_id)}
                      >
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer transition-colors">
                            <div className="flex items-center gap-3">
                              {expandedKb === item.kb_id ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
                              )}
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-mono font-medium text-primary">{item.kb_id}</span>
                                  {item.is_critical && (
                                    <span className="badge bg-red-500/10 text-red-500 text-xs">Crítico</span>
                                  )}
                                  {item.tipo && (
                                    <span className="badge badge-outline text-xs">{item.tipo}</span>
                                  )}
                                </div>
                                {item.titulo && (
                                  <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{item.titulo}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <div className="flex items-center gap-1" title="Servidores">
                                <Server className="h-4 w-4 text-blue-500" />
                                <span>{item.servers_count}</span>
                              </div>
                              <div className="flex items-center gap-1" title="Workstations">
                                <Monitor className="h-4 w-4 text-green-500" />
                                <span>{item.workstations_count}</span>
                              </div>
                              <span className="font-medium">{item.equipos_count} equipos</span>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="bg-muted/30 p-4 border-t border-border">
                            {kbDevicesLoading ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
                              </div>
                            ) : (
                              <>
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-muted-foreground">
                                      <th className="pb-2">EQUIPO</th>
                                      <th className="pb-2">TIPO</th>
                                      <th className="pb-2">SITE</th>
                                      <th className="pb-2">FECHA INSTALACIÓN</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {kbDevices.items.map((device, idx) => (
                                      <tr key={idx} className="border-t border-border/50">
                                        <td className="py-2 font-mono">{device.equipo}</td>
                                        <td className="py-2">
                                          {device.tipo_equipo === 'Servidor' ? (
                                            <span className="flex items-center gap-1"><Server className="h-3 w-3 text-blue-500" /> Servidor</span>
                                          ) : (
                                            <span className="flex items-center gap-1"><Monitor className="h-3 w-3 text-green-500" /> Workstation</span>
                                          )}
                                        </td>
                                        <td className="py-2 text-muted-foreground">{device.site_name || '-'}</td>
                                        <td className="py-2 text-muted-foreground">{formatDate(device.fecha_instalacion)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {kbDevices.pages > 1 && (
                                  <div className="flex justify-end gap-2 mt-3">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      disabled={kbDevices.page === 1}
                                      onClick={() => fetchKbDevices(expandedKb, kbDevices.page - 1)}
                                    >
                                      <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <span className="text-sm py-1">{kbDevices.page} / {kbDevices.pages}</span>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      disabled={kbDevices.page === kbDevices.pages}
                                      onClick={() => fetchKbDevices(expandedKb, kbDevices.page + 1)}
                                    >
                                      <ChevronRight className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Pagination */}
            {installedData.total > 0 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  {installedData.total} KBs encontrados
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchInstalled(installedData.page - 1)}
                    disabled={installedData.page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    {installedData.page} / {installedData.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchInstalled(installedData.page + 1)}
                    disabled={installedData.page === installedData.pages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default PatchesReport;
